/* THE DEMO VIDEO'S TWO PASSES — SHRINK WHEN IT IS TOO BIG, THEN UPLOAD WITH A VISIBLE PROGRESS
 * (his points, 2026-09-23: *"when uploading a video why we don't have a loading thing around the
 * camera icon to see progress? And also, if a video exceeds 200mb why not be able to convert it
 * and make it 200 or even 199mb to be sure? … Can mention about it when it's uploading that it's
 * being converted and from this number of mb it's being converted to 200mb"*).
 *
 * 🔴 ONE RING, TWO PASSES, EACH LABELLED. Shrinking and uploading are different work at different
 * speeds, so one blended percentage would sit still for minutes and then leap — a number that does
 * not mean anything. So the ring fills once for "Shrinking 480 MB to 200 MB" and then again, from
 * zero, for "Uploading"; the words say which pass the ring belongs to.
 *
 * ⚠️ THE SHRINK RUNS IN THE BROWSER, ON THE COACH'S OWN MACHINE (WebCodecs, via `mediabunny`, loaded
 * only when a file is actually too big — nobody else downloads it). Nothing is sent anywhere to be
 * converted, and there is no server to pay for. It re-encodes the picture at a CONSTANT bitrate
 * chosen so the file lands under the bucket's 200 MiB; the sound is copied as it is.
 *
 * ⚠️ THE UPLOAD IS SUPABASE'S RESUMABLE (TUS) ENDPOINT, with the library's plain upload as the
 * fallback (see `uploadResumable`). Same bucket, same owner folder, same policy — the bucket decides.
 */
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from "./supabase";
import { VIDEO_SIZE_LIMIT_BYTES } from "./coachExercise";

const MIB = 1_048_576;

/** What the shrink aims for: 199 MiB — under the 200 MiB bucket with a mebibyte to spare, his "199
 *  to be sure". The ring's words still say 200, the number the coach knows as the limit. */
export const SHRINK_TARGET_BYTES = 199 * MIB;

/** Megabytes as a coach reads them, rounded UP so a file is never said to be smaller than it is. */
export function mbLabel(bytes: number): string {
  return `${Math.ceil(bytes / MIB)} MB`;
}

/** The sound is copied, not re-encoded; this is what it is ALLOWED to cost in the budget. A phone's
 *  AAC is 128–256 kbit/s, so the ceiling is taken rather than a guess at the middle. */
export const AUDIO_ALLOWANCE_BPS = 256_000;

/** How the picture is re-encoded to land under `target`. The container and a constant-bitrate
 *  encoder's own overshoot are paid for by aiming 6% under; a very long clip is also made smaller
 *  in pixels, because a picture starved of bits looks worse at 1080p than at 720p. Null when even
 *  the floor cannot fit — a clip so long that no sensible bitrate brings it under the limit. */
export function shrinkPlan(durationSeconds: number, width: number, target = SHRINK_TARGET_BYTES):
    { videoBitrate: number; width: number | null } | null {
  if (!(durationSeconds > 0)) return null;
  const total = (target * 8 * 0.94) / durationSeconds;
  const videoBitrate = Math.floor(total - AUDIO_ALLOWANCE_BPS);
  if (videoBitrate < 300_000) return null;
  const resize = videoBitrate < 1_000_000 ? 854 : videoBitrate < 2_500_000 ? 1280 : null;
  return { videoBitrate, width: resize !== null && width > resize ? resize : null };
}

/** Where the work is: which pass, and how far through it (0–1). */
export type VideoProgress =
  | { phase: "shrinking"; fraction: number; fromBytes: number }
  | { phase: "uploading"; fraction: number };

/** The ring's words — one sentence per pass, the percentage on its face. */
export function progressLabel(p: VideoProgress): string {
  const pct = `${Math.floor(Math.min(1, Math.max(0, p.fraction)) * 100)}%`;
  return p.phase === "shrinking"
    ? `Shrinking ${mbLabel(p.fromBytes)} to 200 MB · ${pct}`
    : `Uploading · ${pct}`;
}

/** Re-encode `file` to fit under the bucket. Throws with a sentence a coach can act on. */
export async function shrinkVideo(file: File, onProgress: (fraction: number) => void,
                                  target = SHRINK_TARGET_BYTES): Promise<File> {
  if (typeof VideoEncoder === "undefined") {
    throw new Error("This browser cannot shrink a video. Export it under 200 MB and upload that.");
  }
  const mb = await import("mediabunny");
  const input = new mb.Input({ source: new mb.BlobSource(file), formats: mb.ALL_FORMATS });
  const duration = await input.computeDuration();
  const track = await input.getPrimaryVideoTrack();
  const width = track === null ? 0 : track.displayWidth;
  // Two tries: if the encoder overshoots the first time, once more at 85% of the bitrate.
  for (const squeeze of [1, 0.85]) {
    const plan = shrinkPlan(duration, width, target * squeeze);
    if (plan === null) {
      throw new Error(`This video is too long to fit in 200 MB at a watchable quality. Trim it and try again.`);
    }
    const output = new mb.Output({ format: new mb.Mp4OutputFormat({ fastStart: "in-memory" }), target: new mb.BufferTarget() });
    const conversion = await mb.Conversion.init({
      input, output,
      video: {
        codec: "avc",
        quality: new mb.Quality({ bitrate: plan.videoBitrate, bitrateMode: "constant" }),
        ...(plan.width !== null ? { width: plan.width } : {}),
      },
    });
    if (!conversion.isValid) {
      throw new Error("This browser cannot re-encode this video. Export it under 200 MB and upload that.");
    }
    conversion.onProgress = (f) => onProgress(f);
    await conversion.execute();
    const buffer = output.target.buffer;
    if (buffer !== null && buffer.byteLength <= VIDEO_SIZE_LIMIT_BYTES) {
      const name = file.name.replace(/\.[^.]+$/, "") + ".mp4";
      return new File([buffer], name, { type: "video/mp4" });
    }
  }
  throw new Error("The video would not shrink under 200 MB. Trim it and try again.");
}

/** Supabase's resumable endpoint takes the file in pieces of EXACTLY 6 MB — their documented rule
 *  for `/storage/v1/upload/resumable`; any other size is refused. */
export const RESUMABLE_CHUNK_BYTES = 6 * MIB;

/** THE RESUMABLE (TUS) ENDPOINT, on the project's MAIN host. Supabase recommends the direct
 *  `<ref>.storage.supabase.co` host for speed, but the site's Content-Security-Policy names only
 *  `<ref>.supabase.co` in `connect-src` (`netlify.toml`), so the direct host would be BLOCKED by the
 *  browser — a refusal that never reaches the server and reads like a network failure. */
export const RESUMABLE_ENDPOINT = `${SUPABASE_URL}/storage/v1/upload/resumable`;

/** The server's own sentence out of a TUS failure — the JSON body's `message`, else the status —
 *  never a paraphrase (a 42501 names the policy, and hiding it is how the lowercase-path bug survived). */
export function resumableRefusal(status: number | undefined, body: string | undefined): string {
  if (body !== undefined && body !== "") {
    try {
      const parsed = JSON.parse(body) as { message?: string; error?: string };
      const said = parsed.message ?? parsed.error;
      if (typeof said === "string" && said !== "") return said;
    } catch { /* not JSON — fall through to the status */ }
  }
  return status === undefined
    ? "The connection dropped during the upload."
    : `The upload was refused (${status}).`;
}

/** A refusal is final; only a dropped connection or a server hiccup is worth another try. Retrying a
 *  403 would just repeat the refusal five times, slowly. */
export function retriesAfter(status: number | undefined): boolean {
  return status === undefined || status === 409 || status === 423 || status >= 500;
}

/**
 * 🔴 UPLOAD WITH PROGRESS — RESUMABLE (TUS), SUPABASE'S DOCUMENTED PATH FOR LARGE FILES (W62, his
 * second round, 2026-09-23). The first progress upload was a single raw XHR POST and was refused on
 * coachwork.io with *"'alg' (Algorithm) Header Parameter value not allowed"*; it was rolled back the
 * same morning. MEASURED from here with deliberately INVALID tokens (they cannot write): that exact
 * sentence comes back only for a token whose algorithm is neither ES256 nor HS256 (RS256 reproduces
 * it; the project signs ES256 and a forged ES256/HS256 token reads "signature verification failed",
 * at 1 byte AND at 199 MB). What his browser sent is NOT measured — it needs his session.
 *
 * So this path does not stand alone: the editor falls back to the library's own upload on ANY
 * failure here (see `uploadVideo`), which means a coach is never worse off than with no progress.
 * The file goes in 6 MB pieces, each acknowledged, so the ring moves as the bytes actually land, and
 * a dropped connection retries from the last acknowledged piece instead of from zero.
 *
 * ⚠️ NO RESUMING ACROSS ATTEMPTS: `storeFingerprintForResuming: false`. tus-js-client would
 * otherwise continue a previous attempt of the same FILE into the previous attempt's object name,
 * while the editor saves the NEW path — a video row pointing at an object that never finishes.
 */
export async function uploadResumable(bucket: string, path: string, file: Blob, contentType: string,
                                      accessToken: string, onProgress: (fraction: number) => void): Promise<void> {
  const tus = await import("tus-js-client");
  return new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      endpoint: RESUMABLE_ENDPOINT,
      chunkSize: RESUMABLE_CHUNK_BYTES,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      uploadDataDuringCreation: true,
      storeFingerprintForResuming: false,
      removeFingerprintOnSuccess: true,
      headers: {
        authorization: `Bearer ${accessToken}`,
        apikey: SUPABASE_PUBLISHABLE_KEY,
        "x-upsert": "false",
      },
      metadata: { bucketName: bucket, objectName: path, contentType, cacheControl: "3600" },
      onProgress: (sent, total) => onProgress(total > 0 ? sent / total : 0),
      onShouldRetry: (err) => {
        const res = (err as { originalResponse?: { getStatus(): number } | null }).originalResponse;
        return retriesAfter(res?.getStatus());
      },
      onError: (err) => {
        const res = (err as { originalResponse?: { getStatus(): number; getBody(): string } | null }).originalResponse;
        reject(new Error(resumableRefusal(res?.getStatus(), res?.getBody())));
      },
      onSuccess: () => { onProgress(1); resolve(); },
    });
    upload.start();
  });
}
